import React from "react";
import { Alert, AlertTitle, Box, Button, Container, Typography } from "@mui/material";
import { Form, useNavigate, useParams } from "react-router-dom";
import { apiClientHooks } from "../../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { queryClient } from "../../bootstrapping/InitReactQuery";
import { paymentMethodTypeMapping } from "../../utils/option-data";
import GoToDashboardButton from "../../components/GoToDashboardButton";
import ApplicationPaymentExternalForm from "../../components/ApplicationPaymentExternalForm";
import ApplicationPaymentStatusChecker from "../../components/ApplicationPaymentStatusChecker";
import { labelsForEBulkApplicationStatus } from "../../constants/enums";

export const Element: React.FC = () => {
  const navigate = useNavigate()

  const { customer_id, application_id } = useParams<{ customer_id?: string, application_id?: string }>()  
  const customerPathPrefixLeadingSlash = customer_id ? `/${customer_id}` : ''
  if (!application_id) throw new Error('application_id is required')

  // Determine the cost of the application
  const usePriceForEBulkApplicationByID = apiClientHooks.usePriceForEBulkApplicationByID({ params: { id: application_id } })

  const appPricing = usePriceForEBulkApplicationByID.data

  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<string | undefined>(undefined)

  const choosePaymentMethod = (method: string) => {
    // If the method is Stripe, navigate to the Stripe payment page
    if (method === 'stripe') {
      navigate(`${customerPathPrefixLeadingSlash}/applications/${application_id}/payment/stripe`)
    } else
    // Otherwise, set the selected payment method
    {
      setSelectedPaymentMethod(method)
    }
  }

  const payForEBulkApplicationByID = apiClientHooks.usePayForEBulkApplicationByID({
    params: {
      id: application_id,
    },
  })

  const onNoPaymentNecessary = async () => {
    if (!appPricing?.price) throw new Error('Price is required')
    await payForEBulkApplicationByID.mutateAsync({
      payment_method: 'none',
    })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') })
    navigate(`/${customer_id ?? ''}`)
  };

  const onPayByStripe = async () => {
    navigate(`${customerPathPrefixLeadingSlash}/applications/${application_id}/payment/stripe`)
  }

  const onPayByInvoice = async () => {
    if (!appPricing?.price) throw new Error('Price is required')
    await payForEBulkApplicationByID.mutateAsync({
      payment_method: 'invoice',
      payment_pence: appPricing.price.priceInPence,
    })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') })
    navigate(`/${customer_id ?? ''}`)
  }

  return <>
    <Helmet>
      <title>Payment</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Form method="post">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <ApplicationPaymentStatusChecker application_id={application_id} marginBottom={3} />
          <Typography component="h1" variant="h5" marginBottom={3}>
            Payment
          </Typography>
          <Typography component="h1" variant="h6" marginBottom={3} align="center">
            {appPricing?.price.label} - £{((appPricing?.amountToPayInPence ?? 0) / 100).toFixed(2)} to pay
            {(appPricing?.alreadyPaidInPence ?? 0) > 0 && <>
              <br />
              <em>(already paid: £{((appPricing?.alreadyPaidInPence ?? 0) / 100).toFixed(2)})</em>
            </>}
          </Typography>
          {appPricing && appPricing.applicationStatus !== 'awaiting_payment' && (
            <Alert severity="error" sx={{ marginBottom: 3 }}>
              <AlertTitle>Application Not Payable</AlertTitle>
              Application status is: <strong>{labelsForEBulkApplicationStatus[appPricing.applicationStatus]}</strong>.
              <GoToDashboardButton variant="contained" sx={{ marginTop: 2 }} />
            </Alert>
          )}
          {appPricing?.allowedPaymentMethods.length === 0
            ? (
              <Alert severity="info" sx={{ marginBottom: 3 }}>
                <AlertTitle>No Payment Methods Available</AlertTitle>
                This application does not have any payment methods available. This usually means the application has already been paid for.
                <GoToDashboardButton variant="contained" sx={{ marginTop: 2 }}>
                  Return to Dashboard
                </GoToDashboardButton>
              </Alert>
            )
            : (
              <Typography variant="body1" marginBottom={2}>
                Please choose a payment method to continue:
              </Typography>
            )
          }
          {appPricing?.allowedPaymentMethods.map(method => (
            <Button
              key={method}
              variant='contained'
              color={selectedPaymentMethod === method ? 'success' : 'primary'}
              sx={{ marginBottom: 2 }}
              onClick={() => choosePaymentMethod(method)}
            >
              {paymentMethodTypeMapping[method].label}
            </Button>
          ))}
          {selectedPaymentMethod === 'none' && (
            <Alert severity="success" sx={{ marginTop: 2 }}>
              <AlertTitle>No Payment Necessary</AlertTitle>
              There is no payment necessary for this application.
              <br />
              <br />
              <Button variant='contained' onClick={onNoPaymentNecessary}>
                Continue without Payment
              </Button>
            </Alert>
          )}
          {selectedPaymentMethod === 'stripe' && (
            <Alert severity="success" sx={{ marginTop: 2 }}>
              <AlertTitle>Stripe Payment</AlertTitle>
              To pay by card using Stripe, please click the button below.
              <br />
              <br />
              <Button variant='contained' onClick={onPayByStripe}>
                Pay by Stripe
              </Button>
            </Alert>
          )}
          {selectedPaymentMethod === 'invoice' && (
            <Alert severity="success" sx={{ marginTop: 2 }}>
              <AlertTitle>Invoice Payment</AlertTitle>
              Please confirm that you wish for this application to be paid for by invoice.
              <br />
              <br />
              <Button variant='contained' onClick={onPayByInvoice}>
                Pay by Invoice
              </Button>
            </Alert>
          )}
          {selectedPaymentMethod === 'external_stripe_paymentlink' && (
            <Alert severity="success" sx={{ marginTop: 2 }}>
              <AlertTitle>External (Stripe - Payment Link)</AlertTitle>
              <ApplicationPaymentExternalForm
                application_id={application_id}
                price={appPricing?.price.priceInPence}
                payment_method='external_stripe_paymentlink'
                payment_reference_text={
                  <Typography component="p" variant="body2">
                    Once the customer has used a payment link to pay for this application, look up the transaction in Stripe and find the Payment Intent (<pre style={{ display: 'inline' }}>pi_ number</pre>) then use it as the payment reference.
                  </Typography>
                }
                buttonText='Paid Externally'
              />
            </Alert>
          )}
          {selectedPaymentMethod === 'external_creditcard' && (
            <Alert severity="success" sx={{ marginTop: 2 }}>
              <AlertTitle>External (Credit Card)</AlertTitle>
              <ApplicationPaymentExternalForm
                application_id={application_id}
                price={appPricing?.price.priceInPence}
                payment_method='external_creditcard'
                payment_reference_text={
                  <Typography component="p" variant="body2">
                    Once the customer has used a paid using a manual credit card system to pay for this application, specify a payment reference which relates to the transaction.
                  </Typography>
                }
                buttonText='Paid Externally'
              />
            </Alert>
          )}
          {selectedPaymentMethod === 'external_other' && (
            <Alert severity="success" sx={{ marginTop: 2 }}>
              <AlertTitle>External (Other)</AlertTitle>
              <ApplicationPaymentExternalForm
                application_id={application_id}
                price={appPricing?.price.priceInPence}
                payment_method='external_other'
                payment_reference_text={
                  <Typography component="p" variant="body2">
                    If payment has been made by any other external / manual method such as cash or cheque or bank transfer, use a payment reference that relates to the transaction.
                  </Typography>
                }
                buttonText='Paid Externally'
              />
            </Alert>
          )}
        </Box>
      </Form>
    </Container>
  </>
}

export default Element
